import { ChevronUpIcon, PlusIcon } from '@heroicons/react/20/solid';
import { useEffect, useMemo, useRef, useState } from 'react';

import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import OrganizationAddressFormModal from 'src/features/organization/organization-address/ui/OrganizationAddressFormModal';
import { captureException } from 'src/services/reporting';
import {
  AddressBlock,
  AiIcon,
  AppImage,
  MatchTag,
  QuoteStatusTag
} from 'src/shared/components';
import {
  CTA_BULK_ORDERING,
  CTA_BUY_PRE_MADE_SAMPLE,
  CTA_SUPPORT,
  CtaTemplateId
} from 'src/shared/constants/update';
import { Address, Organization } from 'src/shared/types/user';
import { formatMoney } from 'src/shared/utils/formatMoney';
import getDiscountLabel from 'src/shared/utils/getDiscountLabel';
import {
  pricingUnitPriceSuffix,
  pricingUnitQuantitySuffix
} from 'src/shared/utils/pricingUnit';

import AiCardBorder from 'src/shared/components/AiCardBorder';
import { NEXT_SAMPLE_50_DISCOUNT } from 'src/shared/components/DiscountBanner';
import {
  addToast,
  cn,
  Divider,
  Tooltip,
  useDisclosure
} from 'src/shared/components/ui';
import {
  ButtonV2,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  Tab,
  TabsV2,
  Typography
} from 'src/shared/components/ui';

import useProductUpdateInputModal from '../../hooks/useProductUpdateInputModal';
import { useSampling } from '../../hooks/useSampling';
import { useSamplingIntent } from '../../hooks/useSamplingIntent';
import { useUpdateSampling } from '../../hooks/useUpdateSampling';
import QuoteDetailTabContent, {
  LabelValue
} from '../detail-modal/QuoteDetailTabContent';
import MarkdownContent from '../updates-tab/markdown-content/MarkdownContent';
import AddressSelectionModal from './AddressSelectionModal';
import { CountryFlag } from './CountryFlag';
import SupplierTabContent from './SupplierTabContent';

const splitMatchReview = (matchReview: string | null): string[] => {
  if (matchReview && matchReview.startsWith('#')) {
    const newlineIndex = matchReview.indexOf('\n');

    if (newlineIndex === -1) {
      return ['', matchReview];
    }

    return [
      matchReview.slice(0, newlineIndex).replaceAll('#', ''),
      matchReview.slice(newlineIndex + 1)
    ];
  }

  return ['', matchReview || ''];
};

interface QuoteDetailModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  selectedQuote: any;
}

const QuoteDetailModal: React.FC<QuoteDetailModalProps> = ({
  isOpen,
  onOpenChange,
  selectedQuote
}) => {
  const [selectedTab, setSelectedTab] = useState('details');
  const { isOpen: isOpenPayment, onOpenChange: onOpenChangePayment } =
    useDisclosure();
  const selectedOrganization = useAuthStore(
    (state) => state.selectedOrganization
  );
  const {
    isOpen: isOpenAddress,
    onOpen: onOpenAddress,
    onOpenChange: onOpenChangeAddress
  } = useDisclosure();
  const [selected, setSelected] = useState(
    new Set(
      selectedOrganization?.addresses
        ? [selectedOrganization?.addresses[0]?.name]
        : []
    )
  );
  const [isOpenSelect, setIsOpenSelect] = useState(false);

  const [sampling, setSampling] = useState<any>(null);
  const { data } = useSampling(
    selectedQuote?.product,
    selectedQuote?.sampling_id
  );

  const samplingIntentMutation = useSamplingIntent();
  const updateSamplingMutation = useUpdateSampling(selectedQuote?.product);

  const isPaymentNotYetStarted = useMemo(() => {
    const status = selectedQuote?.sample_payment_status;

    return status === null;
  }, [selectedQuote]);

  const isPaymentStarted = useMemo(() => {
    const status = selectedQuote?.sample_payment_status;

    return status === 'started';
  }, [selectedQuote]);

  const isPaymentCompleted = useMemo(() => {
    const status = selectedQuote?.sample_payment_status;

    return status === 'completed';
  }, [selectedQuote]);

  const [editingAddress, setEditingAddress] = useState<any>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [showCollapseButton, setShowCollapseButton] = useState(false);

  const { setData: setUpdateInputData } = useProductUpdateInputModal();
  const {
    isOpen: isBulkAddressOpen,
    onOpen: openBulkAddress,
    onOpenChange: onBulkAddressOpenChange,
    onClose: closeBulkAddress
  } = useDisclosure();
  const [pendingCtaData, setPendingCtaData] = useState<{
    data: {
      name: string;
      quantity?: number | null;
      supplierCloak?: string | null;
      productQuoteId?: string | null;
    };
    ctaType: CtaTemplateId;
  } | null>(null);

  useEffect(() => {
    if (contentRef.current) {
      setShowCollapseButton(contentRef.current.scrollHeight > 100);
    }
  }, [selectedQuote?.match_review]);

  const handleSelectionChange = (val: any) => {
    if (val.has('new')) {
      onOpenAddress();
    } else {
      if (val.size > 0) {
        setSelected(new Set(val));
      }
    }
  };

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'details':
        return <QuoteDetailTabContent selectedQuote={selectedQuote} />;

      case 'supplier':
        return <SupplierTabContent selectedQuote={selectedQuote} />;

      default:
        return '';
    }
  };

  const addressOptions = useMemo(() => {
    return (
      selectedOrganization?.addresses?.map((address, index) => ({
        key: address.name,
        label: address.name,
        index,
        ...address
      })) || []
    );
  }, [selectedOrganization]);

  const onCreateIntent = () => {
    // just show payment modal if sampling_id is present
    if (
      selectedQuote.sampling_id ||
      sampling ||
      data ||
      selectedQuote.samples?.length > 0
    ) {
      onOpenChangePayment();

      return;
    }

    //trigger the payment link if sampling_id is not present
    samplingIntentMutation.mutate(
      {
        body: {
          product_quote: selectedQuote?.id,
          discount_id: selectedOrganization?.discounts?.[0]
        },
        productId: selectedQuote?.product
      },
      {
        onSuccess: (data: any) => {
          const lastIntent = data.data?.sample_payment_intent;

          if (lastIntent.stripe_payment_link) {
            setSampling(data?.data);
            onOpenChangePayment();
          }
        }
      }
    );
  };

  const onCheckout = () => {
    const selectedAddressName = Array.from(selected)[0];

    if (!selectedAddressName) {
      addToast({
        title: 'Please select a shipping address before checkout.',
        color: 'danger'
      });

      return;
    }

    if (selectedOrganization?.addresses) {
      const address = selectedOrganization?.addresses.find(
        (address) => address.name === selectedAddressName
      );

      if (!address) {
        addToast({
          title: 'Selected address not found',
          color: 'danger'
        });

        return;
      }

      const id =
        sampling?.id ||
        data?.data?.id ||
        selectedQuote?.sampling_id ||
        selectedQuote?.samples?.[0]?.id;

      if (!id) {
        addToast({
          title: 'No sampling id found. Please contact support.',
          color: 'danger'
        });

        return;
      }

      updateSamplingMutation.mutate(
        {
          id,
          body: { address: address as Address }
        },
        {
          onSuccess: (data: any) => {
            if (data?.sample_payment_intent?.stripe_payment_link) {
              onOpenChangePayment();
              window.location.href =
                data?.sample_payment_intent?.stripe_payment_link;
            } else {
              captureException({
                error: new Error(
                  `Payment link not found for quote ${data?.id} - ${data?.name}`
                ),
                extra: {
                  data
                }
              });
              addToast({
                title: 'Payment link not found. Please contact support.',
                color: 'danger'
              });
            }
          }
        }
      );
    }
  };

  const buySampleButtonText = useMemo(() => {
    if (
      selectedQuote?.sample_total_cost_discount?.id === NEXT_SAMPLE_50_DISCOUNT
    ) {
      const discountLabel = getDiscountLabel(
        selectedQuote?.sample_total_cost_discount
      );

      return `Buy sample (${discountLabel})`;
    }

    return 'Buy sample';
  }, [selectedQuote]);

  const [matchReviewTitle, matchReviewContent] = useMemo(() => {
    return splitMatchReview(selectedQuote?.match_review);
  }, [selectedQuote?.match_review]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="3xl"
      className="h-[90%] max-w-[788px]"
    >
      <ModalContent>
        {() => (
          <>
            <ModalBody className="p-0 flex flex-col gap-6 pb-5 overflow-y-auto">
              <div className="flex justify-between items-start p-5 pb-2">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center">
                    <Typography
                      variant="h7"
                      weight="bold"
                      className="text-secondary-text"
                    >
                      Quote
                    </Typography>
                    <div className="size-5 flex items-center justify-center">
                      <div className="size-1 bg-gray-4 rounded-full" />
                    </div>
                    <Typography
                      variant="h7"
                      weight="bold"
                      className="text-secondary-text"
                    >
                      {format(selectedQuote?.created_at, 'MMM dd, yyyy')}
                    </Typography>
                    <div className="size-5 flex items-center justify-center">
                      <div className="size-1 bg-gray-4 rounded-full" />
                    </div>
                    <Typography
                      variant="h7"
                      weight="bold"
                      className="text-secondary-text"
                    >
                      Last updated:{' '}
                      {format(selectedQuote?.updated_at, 'MMM dd, yyyy')}
                    </Typography>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Typography
                      variant="h2"
                      weight="bold"
                      className="min-w-0 break-words mobile:text-2xl"
                    >
                      {selectedQuote.variant}
                    </Typography>
                    {(selectedQuote.match_score ||
                      selectedQuote.recommended) && (
                      <MatchTag
                        score={selectedQuote.match_score}
                        recommended={selectedQuote.recommended}
                      />
                    )}
                    <QuoteStatusTag quote={selectedQuote} />
                  </div>
                </div>
              </div>

              <div className="flex flex-row gap-5 items-center px-5 mobile:flex-col mobile:gap-3 mobile:py-0 mobile:items-stretch">
                <div className="flex flex-col items-center justify-center flex-1 mobile:items-start mobile:flex-none">
                  <Typography
                    variant="body-1"
                    as="p"
                    className="text-secondary-text"
                    weight="medium"
                  >
                    Production Unit Cost
                  </Typography>
                  <Typography variant="h5" as="span" weight="bold">
                    {formatMoney(selectedQuote.price)}
                    {pricingUnitPriceSuffix(selectedQuote.pricing_unit)}
                  </Typography>
                </div>
                <Divider orientation="vertical" className="mobile:hidden" />
                <div className="flex flex-col items-center justify-center flex-1 mobile:items-start mobile:flex-none">
                  <Typography
                    variant="body-1"
                    as="p"
                    className="text-secondary-text"
                    weight="medium"
                  >
                    Quantity
                  </Typography>
                  <Typography variant="h5" as="span" weight="bold">
                    {selectedQuote.quantity}
                    {pricingUnitQuantitySuffix(selectedQuote.pricing_unit)}
                  </Typography>
                </div>
                <Divider orientation="vertical" className="mobile:hidden" />
                <div className="flex flex-col items-center justify-center flex-1 mobile:items-start mobile:flex-none">
                  <Typography
                    variant="body-1"
                    as="p"
                    className="text-secondary-text"
                    weight="medium"
                  >
                    Supplier
                  </Typography>
                  <div className="flex items-center gap-1">
                    <Typography variant="h5" as="span" weight="bold">
                      {selectedQuote.supplier_name}
                    </Typography>
                    <CountryFlag
                      countryCode={selectedQuote.supplier_factory_country}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-start gap-2 px-5">
                {isPaymentCompleted && (
                  <Tooltip
                    content="This quote has already been chosen for sampling."
                    closeDelay={0}
                    className="rounded-lg shadow-md font-medium text-base"
                  >
                    <div className="inline-block">
                      <ButtonV2 isLoading={false} isDisabled={true}>
                        Sample requested
                      </ButtonV2>
                    </div>
                  </Tooltip>
                )}
                {isPaymentStarted && (
                  <ButtonV2
                    isLoading={samplingIntentMutation.isPending}
                    isDisabled={samplingIntentMutation.isPending}
                    onPress={onCreateIntent}
                  >
                    Purchase sample
                  </ButtonV2>
                )}
                {isPaymentNotYetStarted && (
                  <ButtonV2
                    isLoading={samplingIntentMutation.isPending}
                    isDisabled={samplingIntentMutation.isPending}
                    onPress={onCreateIntent}
                  >
                    {buySampleButtonText}
                  </ButtonV2>
                )}
                <ButtonV2
                  onPress={() => {
                    setPendingCtaData({
                      data: {
                        name: selectedQuote.product_name,
                        supplierCloak: selectedQuote.supplier_name,
                        productQuoteId: selectedQuote.id
                      },
                      ctaType: CTA_BUY_PRE_MADE_SAMPLE
                    });
                    openBulkAddress();
                  }}
                  color="secondary"
                >
                  Buy pre-made sample
                </ButtonV2>
                <ButtonV2
                  onPress={() => {
                    setPendingCtaData({
                      data: {
                        name: selectedQuote.product_name,
                        quantity: selectedQuote?.quantity,
                        supplierCloak: selectedQuote.supplier_name,
                        productQuoteId: selectedQuote.id?.toString()
                      },
                      ctaType: CTA_BULK_ORDERING
                    });
                    openBulkAddress();
                  }}
                  color="secondary"
                >
                  Bulk order
                </ButtonV2>
                <ButtonV2
                  onPress={() => {
                    const data = {
                      name: selectedQuote.product_name,
                      supplierCloak: selectedQuote.supplier_name,
                      productQuoteId: selectedQuote.id
                    };

                    setUpdateInputData(data, CTA_SUPPORT);
                  }}
                  color="secondary"
                >
                  Support
                </ButtonV2>
              </div>

              {selectedQuote?.match_review && (
                <div className="relative mx-5">
                  <motion.div
                    ref={contentRef}
                    className={cn(
                      'flex items-start gap-2 rounded-xl bg-white relative z-10 p-4 h-[100px] min-h-[100px]',
                      showCollapseButton && 'cursor-pointer'
                    )}
                    onClick={() => {
                      if (showCollapseButton) {
                        setIsExpanded(!isExpanded);
                      }
                    }}
                    initial={false}
                    animate={{ height: isExpanded ? '100%' : '100px' }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div>
                      <AiIcon isColored />
                    </div>
                    <div className="w-full">
                      <Typography variant="h7" weight="bold">
                        {matchReviewTitle}
                      </Typography>
                      <MarkdownContent markdownString={matchReviewContent} />
                      {showCollapseButton && (
                        <motion.button
                          className="absolute bottom-4 right-4 z-20"
                          onClick={() => setIsExpanded(!isExpanded)}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <motion.div
                            animate={{ rotate: isExpanded ? 0 : 180 }}
                            transition={{ duration: 0.3 }}
                          >
                            <ChevronUpIcon className="size-6" />
                          </motion.div>
                        </motion.button>
                      )}
                    </div>

                    {showCollapseButton && !isExpanded && (
                      <div
                        id="bottomOverlay"
                        className="absolute bottom-0 left-0 right-0 h-[76px] z-10 pointer-events-none"
                        style={{
                          background:
                            'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%)'
                        }}
                      />
                    )}
                  </motion.div>
                  <AiCardBorder />
                </div>
              )}

              <div>
                <div className="px-3">
                  <TabsV2
                    selectedKey={selectedTab}
                    onSelectionChange={(key) => setSelectedTab(key as string)}
                  >
                    <Tab key="details" title="Quote details" />
                    <Tab key="supplier" title="Supplier" />
                  </TabsV2>
                </div>
                <Divider className="w-full h-[1px] m-0 relative top-[-4px]" />
              </div>

              <div>{renderTabContent()}</div>
            </ModalBody>
          </>
        )}
      </ModalContent>

      <Modal
        isOpen={isOpenPayment}
        onOpenChange={onOpenChangePayment}
        size="full"
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex w-full border-b border-gray-4 p-0 h-20">
                <div className="flex justify-between items-center gap-2 w-full max-w-5xl mx-auto px-5 py-4">
                  <Typography variant="h1" weight="bold">
                    {buySampleButtonText}
                  </Typography>
                </div>
              </ModalHeader>
              <ModalBody className="p-0 flex flex-col gap-8 pb-5">
                <div className="py-4 bg-gray-2">
                  <div className="w-full flex gap-10 items-start max-w-5xl mx-auto px-5 mobile:flex-col mobile:gap-4">
                    <div className="flex items-start gap-5">
                      <AppImage
                        className="min-w-[68px] min-h-[68px] w-[68px] h-[68px] object-cover rounded-xl bg-gray-2 border border-gray-4"
                        id={selectedQuote?.id}
                        src={selectedQuote?.image ?? undefined}
                      />
                      <div>
                        <Typography
                          variant="body-1"
                          className="text-secondary-text"
                        >
                          Variant
                        </Typography>
                        <div className="flex items-start gap-4 mt-1 max-w-md mobile:flex-wrap mobile:gap-2">
                          <Typography
                            variant="h6"
                            as="span"
                            weight="bold"
                            className="leading-tight"
                          >
                            {selectedQuote.variant}
                          </Typography>
                          {(selectedQuote.match_score ||
                            selectedQuote.recommended) && (
                            <MatchTag
                              score={selectedQuote.match_score}
                              recommended={selectedQuote.recommended}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    <div>
                      <Typography
                        variant="body-1"
                        className="text-secondary-text"
                      >
                        Supplier
                      </Typography>
                      <div className="flex items-center gap-1 mt-1">
                        <Typography variant="h6" as="span" weight="bold">
                          {selectedQuote.supplier_name}
                        </Typography>
                        <CountryFlag
                          countryCode={selectedQuote.supplier_factory_country}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col flex-1 gap-3 w-full max-w-5xl mx-auto px-5">
                  <Typography variant="h4" as="span" weight="bold">
                    Select shipping address
                  </Typography>

                  <div className="grid grid-cols-1 gap-4">
                    {/* <Input
                      disabled
                      className="pointer-events-none"
                      label="Shipping price"
                      value={`$${selectedQuote?.sample_shipping_cost_marked_up}`}
                    /> */}
                    <Select
                      isOpen={isOpenSelect}
                      setIsOpen={setIsOpenSelect}
                      fullWidth
                      disallowEmptySelection
                      defaultSelectedKeys={['all']}
                      selectedKeys={selected}
                      onSelectionChange={handleSelectionChange}
                      label="Ship sample to"
                      size="lg"
                      items={[
                        ...addressOptions,
                        { label: 'Use new address', key: 'new' }
                      ]}
                      renderValue={(items: any) => {
                        return items.map((item: any) => {
                          const address = addressOptions.find(
                            (address) => address.key === item.key
                          );

                          return (
                            <div
                              className="flex items-center gap-1"
                              key={address?.key}
                            >
                              <Typography variant="body-1">
                                {address?.key}
                              </Typography>
                              <Typography
                                variant="body-1"
                                className="text-secondary-text"
                              >
                                {address?.line_1}, {address?.line_2},{' '}
                                {address?.state}, {address?.city}{' '}
                                {address?.zip_code} {address?.country}
                              </Typography>
                            </div>
                          );
                        });
                      }}
                      customComponent={(item: any) => {
                        if (item.key === 'new') {
                          return (
                            <div>
                              <Typography
                                variant="body-1"
                                weight="medium"
                                className="text-primary flex items-center gap-1"
                              >
                                <PlusIcon className="size-5" /> Use new address
                              </Typography>
                            </div>
                          );
                        }

                        return (
                          <>
                            <AddressBlock address={item} />
                            <Typography
                              onClick={() => {
                                setEditingAddress(item);
                                setIsOpenSelect(false);
                                onOpenChangeAddress();
                              }}
                              variant="body-1"
                              className="text-primary"
                            >
                              Edit address
                            </Typography>
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
              </ModalBody>
              <ModalFooter className="fixed bottom-0 z-[3] bg-white left-0 right-0 flex w-full p-5 border-t shadow-2xl border-gray-4">
                <div className="flex justify-between items-center gap-2 w-full max-w-5xl mx-auto mobile:flex-col mobile:items-stretch mobile:gap-3">
                  <div className="flex items-center gap-12 mobile:grid mobile:grid-cols-2 mobile:gap-x-6 mobile:gap-y-3">
                    <div className="flex flex-col">
                      <Typography
                        variant="body-1"
                        as="p"
                        className="text-secondary-text"
                        weight="medium"
                      >
                        Sample cost
                      </Typography>
                      <Typography variant="h5" as="span" weight="bold">
                        ${selectedQuote.sample_cost_marked_up}
                      </Typography>
                    </div>
                    <div className="flex flex-col">
                      <Typography
                        variant="body-1"
                        as="p"
                        className="text-secondary-text"
                        weight="medium"
                      >
                        Shipping cost
                      </Typography>
                      <Typography variant="h5" as="span" weight="bold">
                        ${selectedQuote.sample_shipping_cost_marked_up}
                      </Typography>
                    </div>
                    <div className="flex flex-col">
                      <Typography
                        variant="body-1"
                        as="p"
                        className="text-secondary-text"
                        weight="medium"
                      >
                        Lead time
                      </Typography>
                      <Typography variant="h5" as="span" weight="bold">
                        {selectedQuote.sample_lead_time_days_with_shipping} days
                      </Typography>
                    </div>
                    <div className="flex flex-col border-l border-gray-4 pl-10 mobile:border-l-0 mobile:pl-0">
                      <LabelValue
                        label="Total sample cost"
                        value={`$${selectedQuote?.sample_total_cost_marked_up}`}
                        largeValue
                        discount={selectedQuote?.sample_total_cost_discount}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mobile:w-full mobile:pr-20">
                    <ButtonV2
                      onPress={onCheckout}
                      isDisabled={
                        selected.size === 0 || updateSamplingMutation.isPending
                      }
                      isLoading={updateSamplingMutation.isPending}
                      className="mobile:w-full"
                    >
                      Checkout
                    </ButtonV2>
                  </div>
                </div>
              </ModalFooter>

              <OrganizationAddressFormModal
                isOpen={isOpenAddress}
                onOpenChange={onOpenChangeAddress}
                selectedAddress={editingAddress}
                selectedOrganization={selectedOrganization as Organization}
                onSuccess={(name) => {
                  setTimeout(() => {
                    handleSelectionChange(new Set([name]));
                  }, 100);
                }}
              />
            </>
          )}
        </ModalContent>
      </Modal>

      <AddressSelectionModal
        isOpen={isBulkAddressOpen}
        onOpenChange={onBulkAddressOpenChange}
        onConfirm={(address) => {
          closeBulkAddress();
          if (pendingCtaData) {
            setUpdateInputData(
              { ...pendingCtaData.data, address },
              pendingCtaData.ctaType
            );
            setPendingCtaData(null);
          }
        }}
        title="Select shipping address"
        confirmLabel="Continue"
      />
    </Modal>
  );
};

export default QuoteDetailModal;
